/**
 * Country-Image Overrides Context.
 *
 * Single shared fetch of /api/country-overrides (bulk endpoint) — used by:
 *   - Home rotating hero card
 *   - CountryCard (listing grid + Compare/Quiz related-countries carousels)
 *   - CountryPageRenderer (detail page hero, og:image, city cards)
 *
 * Returns CMS image URLs (already prefixed with REACT_APP_BACKEND_URL) when an
 * admin override exists, else null — letting consumers fall back to the
 * placeholder URLs in countries.js / data/country-pages/<slug>.js.
 */
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OverridesContext = createContext({
  overrides: {},
  loaded: false,
  refresh: () => {},
});

export function OverridesProvider({ children }) {
  const [overrides, setOverrides] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [bumpKey, setBumpKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/country-overrides`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => { if (!cancelled) { setOverrides(data || {}); setLoaded(true); } })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [bumpKey]);

  const refresh = useCallback(() => setBumpKey((k) => k + 1), []);
  const value = useMemo(() => ({ overrides, loaded, refresh }), [overrides, loaded, refresh]);

  return <OverridesContext.Provider value={value}>{children}</OverridesContext.Provider>;
}

/** Get override URLs + text for a single country slug. Returns { hero, cities, text } */
export function useCountryOverride(slug) {
  const { overrides } = useContext(OverridesContext);
  return useMemo(() => {
    const entry = overrides?.[slug] || {};
    const images = entry.images || {};
    const cities = images.cities || [null, null, null];
    return {
      hero: images.hero ? `${BACKEND_URL}${images.hero}` : null,
      cities: cities.map((u) => (u ? `${BACKEND_URL}${u}` : null)),
      text: entry.text || {},
    };
  }, [overrides, slug]);
}

/** Refresh handler so the admin CMS can trigger a global re-pull after upload. */
export function useOverridesRefresh() {
  return useContext(OverridesContext).refresh;
}
